import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXAMPLES = [
  {
    label: "Financial",
    text: '"I grew up watching my family struggle with money, and for a long time I carried that story as my own. Today I am learning a new language of abundance — and every intentional choice I make is writing a different chapter. I am becoming someone who builds lasting financial security, and it is already in motion."',
  },
  {
    label: "Career",
    text: '"I spent years playing small, afraid to take up space in rooms I belonged in. I am now someone who speaks up, who leads with confidence, and who knows that my voice adds value. The version of me who sits at the table I once only dreamed of is not far away — she is emerging right now."',
  },
  {
    label: "Relationships & Receiving",
    text: '"I learned early that love had to be earned. Today I know that I am worthy of deep, reciprocal connection simply because I exist. I am opening — slowly, bravely — to being fully seen, and I am already attracting relationships that reflect how far I have come."',
  },
];

function weave({ from_text, now_text, going_text }) {
  const f = (from_text || "").trim();
  const n = (now_text || "").trim();
  const g = (going_text || "").trim();
  const parts = [];
  if (f) parts.push(f.replace(/\s+/g, " "));
  if (n) {
    const conn = parts.length ? " Today, " : "";
    parts.push(`${conn}${n.replace(/^I /i, "I ").replace(/\s+/g, " ")}`);
  }
  if (g) {
    const conn = parts.length ? " And " : "";
    parts.push(`${conn}${g.replace(/^I /i, "I am ").replace(/^I am am /i, "I am ").replace(/\s+/g, " ")}`);
  }
  let combined = parts.join(" ").trim();
  // Tidy
  combined = combined.replace(/\s+([.,;:!?])/g, "$1").replace(/\s{2,}/g, " ");
  // Ensure ends with a period
  if (combined && !/[.!?]$/.test(combined)) combined += ".";
  return combined;
}

export default function BridgeAffirmations() {
  const [from_text, setFrom] = useState("");
  const [now_text, setNow] = useState("");
  const [going_text, setGoing] = useState("");
  const [woven, setWoven] = useState("");
  const [saved, setSaved] = useState([]);
  const [savingState, setSavingState] = useState("idle"); // idle | saving | saved
  const [copyState, setCopyState] = useState("idle");
  const [emailState, setEmailState] = useState("idle"); // idle | sending | sent | error
  const [emailMsg, setEmailMsg] = useState("");

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bridge/affirmations`);
      setSaved(res.data?.items || []);
    } catch (_err) { /* ignore */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const generate = () => {
    const text = weave({ from_text, now_text, going_text });
    setWoven(text);
    setSavingState("idle");
    setCopyState("idle");
  };

  const reweave = () => generate();

  const save = async () => {
    if (!woven.trim()) return;
    setSavingState("saving");
    try {
      await axios.post(`${API}/bridge/affirmations`, {
        from_text, now_text, going_text, woven_text: woven,
      });
      setSavingState("saved");
      refresh();
    } catch (err) {
      setSavingState("idle");
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.detail || "Could not save. Please try again.");
    }
  };

  const copy = async () => {
    if (!woven.trim()) return;
    try {
      await navigator.clipboard.writeText(woven);
      setCopyState("copied");
      setTimeout(() => setCopyState("idle"), 1800);
    } catch (_e) { /* ignore */ }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/bridge/affirmations/${id}`);
      refresh();
    } catch (_err) { /* ignore */ }
  };

  const emailPdf = async () => {
    if (!woven.trim()) return;
    setEmailState("sending");
    setEmailMsg("");
    try {
      const res = await axios.post(`${API}/bridge/affirmations/email-pdf`, {
        from_text, now_text, going_text, woven_text: woven,
      });
      setEmailState("sent");
      setEmailMsg(`Sent to ${res.data?.to || "your inbox"} — check for a printable PDF.`);
      refresh();
    } catch (err) {
      setEmailState("error");
      setEmailMsg(err?.response?.data?.detail || "Could not email PDF. Please try again.");
    }
  };

  return (
    <div className="bridge-panel" data-testid="bridge-panel-affirmations">
      <h2 className="bridge-panel-heading"><em>Bridge</em> Affirmations</h2>
      <p className="bridge-panel-sub">A bridge affirmation honours where you came from, acknowledges where you are, and reaches — believably — toward where you're going. It's not a lie. It's a living truth.</p>

      <div className="bridge-intro">
        <strong>The science behind bridge affirmations:</strong> traditional affirmations often backfire because our subconscious rejects statements it knows aren't true yet. Bridge affirmations work by starting from your real current truth, validating the journey, and reaching forward in a way that feels emotionally honest — creating genuine neural pathways toward your vision without triggering resistance.
      </div>

      <div className="aff-builder">
        <div className="aff-col">
          <div className="aff-col-icon">🌱</div>
          <p className="aff-col-title">Where I came from</p>
          <p className="aff-col-hint">Honour your origin. What have you overcome or grown through?</p>
          <textarea value={from_text} onChange={(e) => setFrom(e.target.value)}
            placeholder="e.g. I grew up not knowing how to manage money, and for years I felt behind…"
            data-testid="aff-from" />
        </div>
        <div className="aff-col">
          <div className="aff-col-icon">✦</div>
          <p className="aff-col-title">Where I am now</p>
          <p className="aff-col-hint">Your honest present. What's true and real right now?</p>
          <textarea value={now_text} onChange={(e) => setNow(e.target.value)}
            placeholder="e.g. I am learning to trust myself with money and taking new steps every week…"
            data-testid="aff-now" />
        </div>
        <div className="aff-col">
          <div className="aff-col-icon">🌟</div>
          <p className="aff-col-title">Where I am going</p>
          <p className="aff-col-hint">Your vision. State it as already in motion, not distant future.</p>
          <textarea value={going_text} onChange={(e) => setGoing(e.target.value)}
            placeholder="e.g. I am becoming someone who builds wealth with ease and confidence…"
            data-testid="aff-going" />
        </div>
      </div>

      <button className="bridge-btn" onClick={generate} type="button" data-testid="aff-generate">
        Weave My Bridge Affirmation ✦
      </button>

      <div className={`aff-output ${woven ? "visible" : ""}`} data-testid="aff-output">
        <p className="aff-output-label">Your Bridge Affirmation</p>
        <p className="aff-text" data-testid="aff-result">{woven}</p>
        <div className="aff-actions">
          <button className="bridge-btn bridge-btn-gold" onClick={save} disabled={savingState === "saving"} data-testid="aff-save">
            {savingState === "saving" ? "Saving…" : savingState === "saved" ? "✦ Saved" : "Save This Affirmation"}
          </button>
          <button className="bridge-btn" onClick={emailPdf} disabled={emailState === "sending"} type="button" data-testid="aff-email-pdf" style={{ width: "auto", padding: "11px 18px", fontSize: 13 }}>
            {emailState === "sending" ? "Sending…" : emailState === "sent" ? "✓ Sent" : "✦ Email me a printable PDF"}
          </button>
          <button className="bridge-btn bridge-btn-ghost" onClick={copy} type="button" data-testid="aff-copy">
            {copyState === "copied" ? "✓ Copied" : "Copy to Clipboard"}
          </button>
          <button className="bridge-btn bridge-btn-ghost" onClick={reweave} type="button" data-testid="aff-reweave">
            Reweave ↺
          </button>
        </div>
        {emailMsg && (
          <div data-testid="aff-email-msg"
            style={{ marginTop: 10, fontSize: 13, color: emailState === "error" ? "#b91c1c" : "var(--bridge-sage)" }}>
            {emailMsg}
          </div>
        )}
        <div style={{ marginTop: 18, padding: "12px 14px", borderRadius: 10,
          background: "rgba(212,120,142,0.06)", borderLeft: "3px solid var(--bridge-blush)",
          fontSize: 13, lineHeight: 1.6, color: "var(--bridge-ink)", fontStyle: "italic" }}
          data-testid="aff-revisit-note">
          ✦ Come back often. What feels true today may need new language in three months — that's the practice. Re-weave this affirmation as you grow so it grows with you.
        </div>
      </div>

      {saved.length > 0 && (
        <div className="saved-affirmations">
          <p className="grat-history-label">Your saved affirmations</p>
          {saved.map((a) => (
            <div className="saved-aff-card" key={a.id} data-testid={`aff-saved-${a.id}`}>
              <div className="saved-aff-card-text">"{a.woven_text}"</div>
              <button onClick={() => remove(a.id)} aria-label="Remove" title="Remove">×</button>
            </div>
          ))}
        </div>
      )}

      <div className="examples-section">
        <p className="examples-title">✦ Examples of powerful bridge affirmations</p>
        <div className="example-cards">
          {EXAMPLES.map((ex) => (
            <div className="example-card" key={ex.label}>
              <p className="ex-card-label">{ex.label}</p>
              <p className="ex-card-text">{ex.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
